/**
 * Put text on the clipboard, including text that is still being fetched.
 *
 * A browser lets a page write to the clipboard in answer to a press, and Safari counts the press
 * as spent once the handler awaits anything: fetching fifty transcripts and only then calling
 * `writeText` leaves the clipboard as it was. Handing the clipboard a `ClipboardItem` whose text is
 * a promise keeps the press: the write starts inside the handler and completes when the text
 * arrives. Chrome takes that form too.
 *
 * Two fallbacks, in order. `writeText` once the text is in hand, for a browser without
 * `ClipboardItem`. And `execCommand("copy")` from a hidden field, for a page not served over HTTPS
 * or from localhost — the console opened by a LAN address — where `navigator.clipboard` does not
 * exist at all.
 */
export async function copyText(text: string | Promise<string>): Promise<void> {
  if (typeof text !== "string" && canWriteLater()) {
    // The fetch's own failure is the one worth reporting, not the clipboard's "write failed" that
    // follows from it, so it is caught on the way through and rethrown.
    let failed = false;
    let failure: unknown;
    const blob = text.then(
      (value) => new Blob([value], { type: "text/plain" }),
      (error: unknown) => {
        failed = true;
        failure = error;
        throw error;
      },
    );
    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      return;
    } catch {
      if (failed) throw failure;
      // The clipboard refused the promise form. The text is in hand by now: try the others.
    }
  }
  const value = await text;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Blocked: an unfocused window, or a press that has expired. The field may still work.
    }
  }
  if (!copyFromField(value)) {
    throw new Error("The browser did not let the page copy. Try again with this window in front.");
  }
}

function canWriteLater(): boolean {
  return (
    typeof ClipboardItem !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.write === "function"
  );
}

function copyFromField(value: string): boolean {
  const field = document.createElement("textarea");
  field.value = value;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.top = "0";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.remove();
  }
}
